import { useRef } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { Check, MessageSquare, Zap, Star, LayoutDashboard, Inbox, ArrowRight } from "lucide-react";
import { FadeUp, CardHover } from "../hooks/motion";
import { CALCOM_DEMO_URL } from "../config/demo";

const FEATURES = [
  {
    icon: <MessageSquare className="w-8 h-8 text-[#71DED6]" />,
    badge: "SMS Quoting",
    headline: "Send a quote in 60 seconds. Get a response in minutes.",
    problem: "Your customer texted three companies. Two didn't respond for hours. You responded in 60 seconds with a real price. Who do you think they booked?",
    benefit: "CleanBid lets you build and send a professional SMS quote in under a minute — from your phone, on the job, between stops. No desktop required.",
    bullets: [
      "Build quotes directly from saved service templates",
      "Send via your dedicated business SMS number",
      "Customer gets a clean, mobile-friendly quote",
      "One tap to accept — job creates automatically",
      "Works from any device, any location",
    ],
    metric: "Businesses using CleanBid send quotes 3x faster than those relying on email or callbacks.",
    testid: "feature-sms-quoting",
  },
  {
    icon: <Zap className="w-8 h-8 text-[#6B5CF6]" />,
    badge: "Follow-Up Automation",
    headline: "The follow-up you never send is the job you don't win.",
    problem: "Most leads need 2–3 touches before they commit. Most service businesses send one quote and never follow up. That's not a lead problem — it's a system problem.",
    benefit: "CleanBid runs follow-up sequences automatically. Timed, professional, and completely hands-off. Leads stay warm without you lifting a finger.",
    bullets: [
      "Day 1, Day 3, Day 5 sequences out of the box",
      "Fully customizable message content and timing",
      "Stops automatically when customer responds",
      "Tracks open and response rates per sequence",
      "Available on Growth and Pro plans",
    ],
    metric: "47% more open quotes convert to booked jobs when auto follow-ups are enabled.",
    testid: "feature-followup",
  },
  {
    icon: <Star className="w-8 h-8 text-amber-400" />,
    badge: "Review Automation",
    headline: "Turn every completed job into a 5-star review.",
    problem: "You do great work. You just never ask. Or you ask awkwardly in person while packing up. Your customers mean to leave a review — they just forget.",
    benefit: "The moment you mark a job complete in CleanBid, a review request goes out automatically. Right time, right message, no friction.",
    bullets: [
      "Auto-send on job completion — zero manual steps",
      "Links directly to Google, Yelp, or Facebook",
      "Customizable message copy",
      "Track review collection rate over time",
      "Included on all plans",
    ],
    metric: "CleanBid customers collect 4.2x more reviews in their first 60 days.",
    testid: "feature-reviews",
  },
  {
    icon: <LayoutDashboard className="w-8 h-8 text-[#71DED6]" />,
    badge: "Job Tracking",
    headline: "Lead to completion in one view.",
    problem: "You're running jobs off a whiteboard, a notes app, and your memory. Something always slips. A lead you forgot to follow up on. A job you never invoiced.",
    benefit: "CleanBid's pipeline view shows you every job, every stage, in one clean screen. Lead → Quoted → Booked → Complete. Nothing falls through.",
    bullets: [
      "Visual pipeline from lead to completion",
      "Status updates via SMS or app",
      "Job notes and customer history in one place",
      "See overdue follow-ups at a glance",
      "Works across your whole team",
    ],
    metric: "Teams using CleanBid's job tracker close 28% more jobs in the same time period.",
    testid: "feature-job-tracking",
  },
  {
    icon: <Inbox className="w-8 h-8 text-[#6B5CF6]" />,
    badge: "Unified Inbox",
    headline: "Every conversation. One screen.",
    problem: "You're bouncing between text messages, Facebook Messenger, emails, and voicemails. You miss a message. The customer moves on.",
    benefit: "Every customer conversation, regardless of channel, lives in CleanBid's unified inbox. Full context, full history, every time.",
    bullets: [
      "All SMS conversations in one place",
      "Customer history always visible",
      "Quick-reply templates for speed",
      "Assign conversations to team members",
      "Never lose a lead to a missed message",
    ],
    metric: "Average response time drops from 4.2 hours to under 8 minutes with the unified inbox.",
    testid: "feature-inbox",
  },
];

export default function FeaturesPage() {
  usePageTitle("Features | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <section className="relative py-24 md:py-32 text-center overflow-hidden">
        {/* Brand mark watermark — right side, behind content */}
        <img
          src="/cleanbid-logo-transparent.png"
          alt=""
          aria-hidden="true"
          className="absolute right-0 z-0 pointer-events-none select-none w-[240px] h-[240px] md:w-[400px] md:h-[400px] opacity-[0.05] md:opacity-[0.06]"
          style={{ top: "50%", transform: "translateY(-50%) rotate(8deg)" }}
        />
        <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
          <FadeUp>
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Features</span>
          </FadeUp>
          <FadeUp delay={60}>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-[#F4F4F8] mt-4 mb-6">
              Built to close. <br />
              <span className="text-gradient-green">Not to impress.</span>
            </h1>
          </FadeUp>
          <FadeUp delay={120}>
            <p className="text-lg text-[#8B8BA7] max-w-2xl mx-auto leading-relaxed">
              Every feature in CleanBid exists for one reason: to help you close more jobs and collect more reviews, automatically. No bloat. No fluff.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Features */}
      <div className="divide-y divide-[#1E1E2E]">
        {FEATURES.map((feat, i) => (
          <section
            key={i}
            data-testid={feat.testid}
            className="py-20 md:py-28"
          >
            <div className="container mx-auto px-4 md:px-6 max-w-6xl">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${i % 2 === 1 ? "" : ""}`}>
                <FadeUp className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-[#111118] border border-[#1E1E2E] flex items-center justify-center">
                        {feat.icon}
                      </div>
                      <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">{feat.badge}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F4F4F8]">
                      {feat.headline}
                    </h2>
                    <p className="text-[#8B8BA7] leading-relaxed">{feat.problem}</p>
                    <p className="text-[#F4F4F8] leading-relaxed">{feat.benefit}</p>
                    <ul className="space-y-3">
                      {feat.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm text-[#8B8BA7]">
                          <Check className="w-4 h-4 text-[#71DED6] mt-0.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-[#111118] border border-[#71DED6]/20 rounded-xl p-4">
                      <p className="text-sm text-[#71DED6] font-medium">{feat.metric}</p>
                    </div>
                  </div>
                </FadeUp>

                <FadeUp delay={120} className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-8 space-y-4">
                    <div className="flex items-center gap-2 pb-4 border-b border-[#1E1E2E]">
                      {feat.icon}
                      <span className="text-sm font-semibold text-[#F4F4F8]">{feat.badge}</span>
                    </div>
                    <div className="space-y-3">
                      {feat.bullets.slice(0, 4).map((b, j) => (
                        <div key={j} className="flex items-center gap-3 bg-[#0A0A0F] rounded-xl px-4 py-3 border border-[#1E1E2E]">
                          <div className="w-5 h-5 rounded-full bg-[#71DED6]/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-[#71DED6]" />
                          </div>
                          <span className="text-sm text-[#F4F4F8]">{b}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#71DED6]/10 border border-[#71DED6]/20 rounded-xl p-4 text-center">
                      <p className="text-lg font-bold text-[#71DED6]">{feat.metric.split(" ").slice(0, 3).join(" ")}</p>
                    </div>
                  </div>
                </FadeUp>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Bottom CTA */}
      <section className="py-24 md:py-32 glow-cta" data-testid="features-cta-section">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mb-4">
              Ready to see it in action?
            </h2>
          </FadeUp>
          <FadeUp delay={80}>
            <p className="text-[#8B8BA7] text-lg mb-8">
              Book a 20-minute demo and we'll show you exactly how CleanBid works for your business.
            </p>
          </FadeUp>
          <FadeUp delay={160}>
            <a
              href={CALCOM_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="features-cta-button"
              className="bg-[#71DED6] text-[#10151D] font-semibold px-10 py-4 rounded-full text-lg hover:scale-[1.02] hover:shadow-glow-green transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Demo <ArrowRight className="w-5 h-5" />
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
